import React, { memo } from 'react';
import { View, StyleSheet, FlatList, Pressable, Text } from 'react-native';

import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';

import { OPComponent, CaseSearch } from '../components';

const LIMIT = 30;
const PAGES = __WEB__ ? 4 : 2;

const PageBtn = memo(function PageBtn(props) {
  function handlePress() {
    props.onPress(props.num);
  }
  return (
    <Pressable
      onPress={handlePress}
      style={[styles.textBubble, props.selected ? THEME.textBubbleSelected : null]}
    >
      <Text style={[styles.timeBubbleText]}>{props.num}</Text>
    </Pressable>
  );
});

const listRef = React.createRef();

export default class CaseSearchPaginated extends OPComponent {
  constructor(props) {
    super(props);
    this.state = {
      toggleToRefreshSearch: false,
      items: [],
      skip: 0,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.props.autoLoadOnScroll &&
      this.state.items !== prevState.items &&
      this.state.items?.length
    ) {
      listRef?.current?.scrollToIndex({ index: 0, animated: false });
    }
  }

  styleWithOverrides(styleName) {
    const props = this.props;
    return [styles[styleName], props.styleOverrides?.[styleName]];
  }

  handleEndReached = () => {
    this.setState({ skip: this.state.items.length });
  };

  prepCaseItems = (cases) => {
    const props = this.props;
    if (props.prepCaseItems) return props.prepCaseItems(cases);
    return cases.map((c) => {
      return {
        label: c.name,
        id: c.caseId,
        item: c,
      };
    });
  };

  handleSearchResult = ({ rows, count }, term) => {
    const props = this.props;
    const items = this.prepCaseItems(rows);
    if (term !== this._lastTerm) {
      this.setState({
        items,
        skip: 0,
        totalCount: count,
      });
    } else {
      this.setState({
        items: !!props.autoLoadOnScroll ? this.state.items.concat(items) : items,
        totalCount: count,
      });
    }
    this._lastTerm = term;
  };

  renderSearch() {
    const props = this.props;
    return (
      <View style={this.styleWithOverrides('searchContainer')}>
        <CaseSearch
          employerId={props.employerId}
          onResult={this.handleSearchResult}
          searchLabel={props.searchLabel}
          includeConsults={props.includeConsults || false}
          toggleToRefreshSearch={props.toggleToRefreshSearch}
          loadOnInit={true}
          skip={this.state.skip}
          limit={LIMIT}
          filters={props.filters}
        />
      </View>
    );
  }

  handlePagePress = (i) => {
    this.setState({ skip: (i - 1) * LIMIT });
  };
  handleNextPress = () => {
    this.setState({ skip: this.state.skip + LIMIT });
  };
  handlePrevPress = () => {
    this.setState({ skip: Math.max(0, this.state.skip - LIMIT) });
  };

  _selectedPage() {
    const skip = this.state.skip;
    return (skip ? Math.ceil(skip / LIMIT) : 0) + 1;
  }
  _pages() {
    return (this.state.totalCount > 0 && Math.ceil(this.state.totalCount / LIMIT)) || 0;
  }

  renderPageBtn = (num, i) => {
    const pages = this._pages();
    const selectedPage = this._selectedPage();
    return (
      <PageBtn key={i} num={num} onPress={this.handlePagePress} selected={selectedPage === num} />
    );
  };
  renderLastPageBtn = () => {
    const pages = this._pages();
    const selectedPage = this._selectedPage();
    return (
      <PageBtn
        key={pages}
        num={pages}
        onPress={this.handlePagePress}
        selected={selectedPage === pages}
      />
    );
  };
  renderFirstPageBtn = () => {
    const selectedPage = this._selectedPage();
    return <PageBtn key={1} num={1} onPress={this.handlePagePress} selected={selectedPage === 1} />;
  };

  renderItem = (rowItem) => {
    const item = rowItem.item;
    if (item.isHeader) {
      return this.props.renderHeader?.(); // Render the header component for the sticky header
    } else {
      return this.props.renderItem(rowItem); // Render normal items
    }
  };
  keyExtractor(rowItem) {
    return rowItem.isHeader ? 'header' : `${rowItem.item.caseId}`;
  }

  render() {
    const props = this.props;
    const pages = this._pages();
    const selectedPage = this._selectedPage();
    const offset = Math.max(Math.min(Math.max(selectedPage - PAGES, 0), pages - PAGES), 0);
    const pageWindow = Array.from({ length: Math.min(PAGES, pages) }, (_, i) => i + offset + 1);
    const more = pageWindow[pageWindow.length - 1] < pages;
    const less = pageWindow[0] > 2;
    return (
      <View style={{ flex: 1 }}>
        <View style={this.styleWithOverrides('container')}>
          <View style={this.styleWithOverrides('searchHeader')}>
            {this.renderSearch()}
            {props.headerRightComponent}
          </View>
          <View style={{ flex: 1 }}>
            <FlatList
              ref={listRef}
              data={[{ key: 'header', isHeader: true }, ...this.state.items]} // Include the header as the first item
              renderItem={this.renderItem}
              stickyHeaderIndices={[0]} // Make the first item (header) sticky
              keyboardShouldPersistTaps="handled"
              keyExtractor={this.keyExtractor}
              contentContainerStyle={this.styleWithOverrides('listContainerStyle')}
              onEndReached={props.autoLoadOnScroll ? this.handleEndReached : null}
              ItemSeparatorComponent={props.ItemSeparatorComponent}
            />
          </View>
        </View>
        {!props.autoLoadOnScroll && (
          <View style={styles.footer}>
            <Pressable
              disabled={selectedPage === 1}
              onPress={this.handlePrevPress}
              style={[styles.nextPrevBubble, selectedPage === 1 ? styles.textBubbleDisabled : null]}
            >
              <Text style={[styles.timeBubbleText]}>Prev</Text>
            </Pressable>
            {pages > PAGES && less && (
              <>
                {this.renderFirstPageBtn()}
                <Text style={styles.dotsStyle}>...</Text>
              </>
            )}
            {pageWindow.map(this.renderPageBtn)}
            {pages > PAGES && more && (
              <>
                <Text style={styles.dotsStyle}>...</Text>
                {this.renderLastPageBtn()}
              </>
            )}
            <Pressable
              disabled={selectedPage === pages}
              onPress={selectedPage < pages ? this.handleNextPress : null}
              style={[
                styles.nextPrevBubble,
                selectedPage === pages ? styles.textBubbleDisabled : null,
              ]}
            >
              <Text style={[styles.timeBubbleText]}>Next</Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  ...THEME,
  searchHeader: {},
  searchContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textBubble: {
    ...THEME.textBubble,
    width: 25,
  },
  nextPrevBubble: {
    ...THEME.textBubble,
    width: 55,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: __WEB__ ? 'center' : 'flex-start',
    paddingLeft: __WEB__ ? 0 : 10,
    alignItems: 'center',
    height: 40,
    marginVertical: 10,
    marginBottom: 20,
  },
  dotsStyle: {
    fontSize: 50,
    marginHorizontal: 10,
    lineHeight: 35,
    paddingBottom: __WEB__ ? 30 : 0,
  },
});
